import React, { useState, useEffect, useCallback } from 'react';
import { BackofficeLayout } from '../../layouts/BackofficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Radio, Play, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight,
  ExternalLink, Search, Filter, Loader2
} from 'lucide-react';
import { api } from '../../context/AuthContext';

// Signal quality badge
function SignalBadge({ score }) {
  if (score >= 80) return <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-green-500/10 border border-green-500/30 text-green-400" data-testid="signal-badge-high">High</span>;
  if (score >= 50) return <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-amber-500/10 border border-amber-500/30 text-amber-400" data-testid="signal-badge-medium">Medium</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono uppercase rounded-sm bg-slate-500/10 border border-slate-500/30 text-slate-400" data-testid="signal-badge-low">Low</span>;
}

// ---------------------------------------------------------------------------
// A) Signal Timeline — sparkline at top
// ---------------------------------------------------------------------------
function SignalTimeline({ timeline, trades }) {
  if (!timeline || timeline.length === 0) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="py-4">
          <p className="text-xs text-slate-500 text-center">No signal data yet</p>
        </CardContent>
      </Card>
    );
  }

  const maxTotal = Math.max(...timeline.map(d => d.total), 1);

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="signal-timeline">
      <CardHeader className="pb-1">
        <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Signal Timeline (30d)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-px h-16">
          {timeline.map((d, i) => (
            <div key={d.date} className="flex-1 flex flex-col items-stretch gap-px" title={`${d.date}: ${d.total} signals`}>
              {trades.map(trade => {
                const val = d[trade] || 0;
                const h = val > 0 ? Math.max(2, (val / maxTotal) * 60) : 0;
                const colors = { electrical: 'bg-orange-500', telecom: 'bg-cyan-500', hvac: 'bg-violet-500', plumbing: 'bg-emerald-500' };
                return <div key={trade} style={{ height: `${h}px` }} className={`${colors[trade] || 'bg-slate-600'} rounded-[1px] transition-all`} />;
              })}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[9px] text-slate-600 font-mono">{timeline[0]?.date}</span>
          <div className="flex gap-3">
            {trades.map(trade => {
              const colors = { electrical: 'text-orange-400', telecom: 'text-cyan-400', hvac: 'text-violet-400', plumbing: 'text-emerald-400' };
              return <span key={trade} className={`text-[9px] font-mono ${colors[trade] || 'text-slate-400'}`}>{trade}</span>;
            })}
          </div>
          <span className="text-[9px] text-slate-600 font-mono">{timeline[timeline.length - 1]?.date}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// C) Filters Panel
// ---------------------------------------------------------------------------
function FiltersPanel({ filters, setFilters, trades }) {
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="filters-panel">
      <CardContent className="py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <select
            value={filters.trade_code}
            onChange={e => setFilters(f => ({ ...f, trade_code: e.target.value }))}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs px-2 py-1.5 rounded-sm"
            data-testid="filter-trade"
          >
            <option value="">All Trades</option>
            {trades.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={filters.source_type}
            onChange={e => setFilters(f => ({ ...f, source_type: e.target.value }))}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs px-2 py-1.5 rounded-sm"
            data-testid="filter-source-type"
          >
            <option value="">All Sources</option>
            <option value="award_notice">Award Notice</option>
            <option value="framework_list">Framework</option>
            <option value="dps_list">DPS</option>
          </select>
          <select
            value={filters.score_range}
            onChange={e => setFilters(f => ({ ...f, score_range: e.target.value }))}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs px-2 py-1.5 rounded-sm"
            data-testid="filter-score"
          >
            <option value="">All Scores</option>
            <option value="high">High (80+)</option>
            <option value="medium">Medium (50-79)</option>
            <option value="low">Low (&lt;50)</option>
          </select>
          <select
            value={filters.prospect_created}
            onChange={e => setFilters(f => ({ ...f, prospect_created: e.target.value }))}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs px-2 py-1.5 rounded-sm"
            data-testid="filter-prospect"
          >
            <option value="">Prospect: Any</option>
            <option value="yes">Created</option>
            <option value="no">Not Created</option>
          </select>
          <Input
            type="date"
            value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-slate-200 text-xs h-8 w-32"
            data-testid="filter-date-from"
          />
          <span className="text-slate-600 text-xs">to</span>
          <Input
            type="date"
            value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="bg-slate-800 border-slate-700 text-slate-200 text-xs h-8 w-32"
            data-testid="filter-date-to"
          />
          {Object.values(filters).some(v => v) && (
            <Button variant="ghost" size="sm" className="text-[10px] text-slate-500 h-7" onClick={() => setFilters({ trade_code: '', source_type: '', score_range: '', prospect_created: '', date_from: '', date_to: '' })} data-testid="clear-filters-btn">
              Clear
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// B) Signal Table
// ---------------------------------------------------------------------------
function SignalTable({ signals, total, skip, limit, onPageChange }) {
  const formatValue = (val) => {
    if (!val) return '--';
    if (val >= 1000000) return `£${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `£${(val / 1000).toFixed(0)}k`;
    return `£${val}`;
  };

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="signal-table">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">
            Signals <span className="text-slate-600 ml-1">({total})</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={skip === 0} onClick={() => onPageChange(Math.max(0, skip - limit))} className="h-6 w-6 p-0 text-slate-400" data-testid="signals-prev-page">
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-[10px] font-mono text-slate-500">{skip + 1}-{Math.min(skip + limit, total)} of {total}</span>
            <Button variant="ghost" size="sm" disabled={skip + limit >= total} onClick={() => onPageChange(skip + limit)} className="h-6 w-6 p-0 text-slate-400" data-testid="signals-next-page">
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-2">Company</th>
                <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Trade</th>
                <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Source</th>
                <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Value</th>
                <th className="text-left text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Award Date</th>
                <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Score</th>
                <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Prospect</th>
                <th className="text-right text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Contact Conf.</th>
                <th className="text-center text-[10px] text-slate-500 uppercase tracking-wider py-2 px-1.5">Action</th>
              </tr>
            </thead>
            <tbody>
              {signals.map(sig => (
                <tr key={sig.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="py-2 px-2">
                    <span className="text-slate-200 font-medium">{sig.company_name}</span>
                    {sig.buyer_name && <span className="block text-[9px] text-slate-600 truncate max-w-[180px]">{sig.buyer_name}</span>}
                  </td>
                  <td className="py-2 px-1.5">
                    <span className="text-[10px] font-mono text-slate-400 uppercase">{sig.trade_code}</span>
                  </td>
                  <td className="py-2 px-1.5">
                    <span className="text-[10px] font-mono text-slate-400">{sig.source_type?.replace('_', ' ')}</span>
                  </td>
                  <td className="py-2 px-1.5 text-right font-mono text-slate-300">{formatValue(sig.contract_value)}</td>
                  <td className="py-2 px-1.5 font-mono text-slate-400">{formatDate(sig.award_date)}</td>
                  <td className="py-2 px-1.5 text-center"><SignalBadge score={sig.signal_score} /></td>
                  <td className="py-2 px-1.5 text-center">
                    {sig.prospect ? (
                      <span className="text-[10px] font-mono text-green-400">{sig.prospect.status}</span>
                    ) : (
                      <span className="text-[10px] font-mono text-slate-600">none</span>
                    )}
                  </td>
                  <td className="py-2 px-1.5 text-right">
                    {sig.prospect?.email_confidence != null ? (
                      <span className={`text-[10px] font-mono ${sig.prospect.email_confidence >= 0.7 ? 'text-green-400' : sig.prospect.email_confidence >= 0.4 ? 'text-amber-400' : 'text-slate-500'}`}>
                        {(sig.prospect.email_confidence * 100).toFixed(0)}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-600">--</span>
                    )}
                  </td>
                  <td className="py-2 px-1.5 text-center">
                    {sig.prospect ? (
                      <a href={`/backoffice/prospects`} className="text-orange-400 hover:text-orange-300 transition-colors" data-testid={`view-prospect-${sig.id}`}>
                        <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    ) : (
                      <span className="text-[10px] text-slate-700">--</span>
                    )}
                  </td>
                </tr>
              ))}
              {signals.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-600 text-xs">No signals found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// D) Scout Control Panel
// ---------------------------------------------------------------------------
function ScoutControlPanel({ configs, onRunScout, onRefresh }) {
  const [running, setRunning] = useState(null);

  const handleRun = async (tradeCode) => {
    setRunning(tradeCode);
    try {
      const resp = await api.post(`/backoffice/signals/scout/run?trade_code=${tradeCode}`);
      const d = resp.data;
      toast.success(`Scout ${tradeCode}: ${d.signals_collected} collected, ${d.signals_inserted} new, ${d.prospects_created} prospects`);
      onRefresh();
    } catch (e) {
      toast.error(`Scout failed: ${e.message}`);
    }
    setRunning(null);
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return 'Never';
    const ms = Date.now() - new Date(ts).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) return `${Math.floor(ms / 60000)}m ago`;
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="scout-control-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Scout Control</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {configs.map(cfg => (
            <div key={cfg.trade_code} className={`rounded-sm border p-2.5 ${cfg.enabled ? 'border-slate-700 bg-slate-800/30' : 'border-slate-800 bg-slate-900/50 opacity-50'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-mono text-slate-200 uppercase tracking-wider">{cfg.trade_code}</span>
                <Badge variant="outline" className={`text-[9px] ${cfg.enabled ? 'text-green-400 border-green-500/30' : 'text-slate-600 border-slate-700'}`}>
                  {cfg.enabled ? 'ON' : 'OFF'}
                </Badge>
              </div>
              <div className="space-y-1 mb-2">
                <div className="text-[9px] text-slate-600 font-mono">Last: {formatTimeAgo(cfg.last_run_at)}</div>
                <div className="text-[9px] text-slate-600 font-mono">Signals: {cfg.total_signals || 0}</div>
              </div>
              <Button
                size="sm"
                disabled={!cfg.enabled || running === cfg.trade_code}
                onClick={() => handleRun(cfg.trade_code)}
                className="w-full h-7 text-[10px] uppercase tracking-wider bg-slate-700 hover:bg-slate-600 text-slate-200 disabled:opacity-30"
                data-testid={`run-scout-${cfg.trade_code}`}
              >
                {running === cfg.trade_code ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-2.5 h-2.5 mr-1" /> Run Scout</>}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// E) Trade Activation Toggles
// ---------------------------------------------------------------------------
function TradeActivation({ configs, onRefresh }) {
  const handleToggle = async (tradeCode, currentEnabled) => {
    try {
      await api.patch(`/backoffice/signals/scout/config/${tradeCode}?enabled=${!currentEnabled}`);
      toast.success(`${tradeCode} ${!currentEnabled ? 'enabled' : 'disabled'}`);
      onRefresh();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    }
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="trade-activation">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-[0.15em] text-slate-400">Trade Activation</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {configs.map(cfg => (
            <div key={cfg.trade_code} className="flex items-center justify-between py-1.5 px-2 rounded-sm hover:bg-slate-800/30 transition-colors">
              <span className="text-xs font-mono text-slate-200 uppercase tracking-wider">{cfg.trade_code}</span>
              <button
                onClick={() => handleToggle(cfg.trade_code, cfg.enabled)}
                className="transition-colors"
                data-testid={`toggle-${cfg.trade_code}`}
              >
                {cfg.enabled ? (
                  <ToggleRight className="w-6 h-6 text-green-400" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-slate-600" />
                )}
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function SignalsDashboard() {
  const [signals, setSignals] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [timeline, setTimeline] = useState([]);
  const [trades, setTrades] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    trade_code: '', source_type: '', score_range: '', prospect_created: '', date_from: '', date_to: '',
  });

  const loadSignals = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.trade_code) params.set('trade_code', filters.trade_code);
    if (filters.source_type) params.set('source_type', filters.source_type);
    if (filters.score_range === 'high') { params.set('score_min', '80'); }
    else if (filters.score_range === 'medium') { params.set('score_min', '50'); params.set('score_max', '79'); }
    else if (filters.score_range === 'low') { params.set('score_max', '49'); }
    if (filters.prospect_created === 'yes') params.set('processed', 'true');
    else if (filters.prospect_created === 'no') params.set('processed', 'false');
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    params.set('skip', skip.toString());
    params.set('limit', '50');

    try {
      const resp = await api.get(`/backoffice/signals?${params.toString()}`);
      setSignals(resp.data.signals || []);
      setTotal(resp.data.total || 0);
    } catch { /* silent */ }
  }, [filters, skip]);

  const loadTimeline = useCallback(async () => {
    try {
      const resp = await api.get('/backoffice/signals/timeline');
      setTimeline(resp.data.timeline || []);
      setTrades(resp.data.trades || []);
    } catch { /* silent */ }
  }, []);

  const loadConfigs = useCallback(async () => {
    try {
      const resp = await api.get('/backoffice/signals/scout/config');
      setConfigs(resp.data.configs || []);
    } catch { /* silent */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSignals(), loadTimeline(), loadConfigs()]);
    setLoading(false);
  }, [loadSignals, loadTimeline, loadConfigs]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Re-fetch signals when filters or page changes
  useEffect(() => { loadSignals(); }, [loadSignals]);

  const handleRefresh = () => { loadAll(); };

  if (loading && signals.length === 0) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-4" data-testid="signals-dashboard">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-orange-500" />
            <h1 className="text-base font-bold text-white tracking-wider" style={{ fontFamily: 'Barlow Condensed' }}>LEAD SIGNALS</h1>
            <span className="text-xs text-slate-600 font-mono ml-2">{total} signals</span>
          </div>
        </div>

        {/* A) Timeline */}
        <SignalTimeline timeline={timeline} trades={trades} />

        {/* D + E) Scout Control + Trade Activation side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <ScoutControlPanel configs={configs} onRunScout={() => {}} onRefresh={handleRefresh} />
          </div>
          <div>
            <TradeActivation configs={configs} onRefresh={handleRefresh} />
          </div>
        </div>

        {/* C) Filters */}
        <FiltersPanel filters={filters} setFilters={(fn) => { setFilters(fn); setSkip(0); }} trades={['electrical', 'telecom', 'hvac', 'plumbing']} />

        {/* B) Signal Table */}
        <SignalTable signals={signals} total={total} skip={skip} limit={50} onPageChange={setSkip} />
      </div>
    </BackofficeLayout>
  );
}
